import { Component } from 'react';
import type { ReactNode } from 'react';

import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkTicket from '@skyscanner-internal/backpack-web/bpk-component-ticket';
import classnames from 'classnames';
import debounce from 'lodash/debounce';

import STYLES from './CardRowLayout.module.scss';

const DEBOUNCE_TIME = 100;

type CardRowLayoutProps = {
  name?: ReactNode;
  badges?: ReactNode;
  distance?: ReactNode;
  mainPrice?: ReactNode;
  otherPrices?: ReactNode;
  cardHighlightClsName?: string;
  reviews?: ReactNode;
  AISummary?: ReactNode;
  onClick?: Function;
  stars?: ReactNode;
  confidentMsg?: ReactNode;
  getImageNode?: Function;
  showReviewImage?: boolean;
  url: string;
};

type CardRowLayoutState = {
  mouseStatus: string;
};

class CardRowLayout extends Component<CardRowLayoutProps, CardRowLayoutState> {
  static defaultProps = {
    name: undefined,
    badges: undefined,
    distance: undefined,
    mainPrice: undefined,
    otherPrices: undefined,
    cardHighlightClsName: undefined,
    reviews: undefined,
    AISummary: undefined,
    onClick: undefined,
    stars: undefined,
    confidentMsg: undefined,
    getImageNode: () => {},
    showReviewImage: false,
  };

  private expImageGalleryEnable: any;

  private debouncedMouseEnter: (event: string) => void;

  constructor(props: CardRowLayoutProps) {
    super(props);

    this.expImageGalleryEnable = false;

    this.state = {
      mouseStatus: this.expImageGalleryEnable ? 'enter' : 'leave',
    };

    this.debouncedMouseEnter = () => {};
  }

  componentDidMount() {
    this.debouncedMouseEnter = debounce(this.mouseHover, DEBOUNCE_TIME);
  }

  mouseHover = (type: string) => {
    if (!this.expImageGalleryEnable) {
      this.setState({ mouseStatus: type });
    }
  };

  onMouseEnter = () => {
    this.debouncedMouseEnter('enter');
  };

  onMouseLeave = () => {
    this.debouncedMouseEnter('leave');
  };

  render() {
    const {
      AISummary,
      badges,
      cardHighlightClsName,
      confidentMsg,
      distance,
      getImageNode,
      mainPrice,
      name,
      onClick,
      otherPrices,
      reviews,
      showReviewImage,
      stars,
      url,
    } = this.props;

    const { mouseStatus } = this.state;

    return (
      <div
        className={classnames(STYLES.CardRowLayout, cardHighlightClsName)}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <BpkCard
          padded={false}
          className={STYLES.CardRowLayout__colCard}
          data-test-id="hotel-card"
          target="_blank"
          atomic={false}
        >
          <div className={classnames(STYLES.CardRowLayout__colLeft)}>
            {getImageNode?.(mouseStatus)}
          </div>
        </BpkCard>
        <BpkTicket
          stub={mainPrice}
          className={classnames(STYLES.CardRowLayout__ticket)}
          role="none"
          stubClassName={classnames(STYLES.CardRowLayout__stub)}
          padded
        >
          <BpkCard
            className={classnames(
              STYLES.CardRowLayout__card,
              STYLES['CardRowLayout__card--whiteBorder'],
            )}
            onClick={() => {
              if (onClick !== null) onClick?.(true, null, true, 'base');
            }}
            target="_blank"
            rel="nofollow"
            padded={false}
            href={url}
          >
            <div className={STYLES.CardRowLayout__baseInfo}>
              <div className={STYLES.CardRowLayout__basicInformation}>
                {name}
                <div
                  className={classnames(STYLES.CardRowLayout__stars, {
                    [STYLES['CardRowLayout__stars--breakLine']]:
                      showReviewImage,
                  })}
                >
                  {stars}
                </div>
                <div className={STYLES.CardRowLayout__badge}>{badges}</div>
              </div>
              <div className={STYLES.CardRowLayout__distance}>{distance}</div>
              {reviews}
              {confidentMsg && (
                <div className={STYLES.CardRowLayout__confidentMsg}>
                  {confidentMsg}
                </div>
              )}
              {AISummary}
            </div>
            {otherPrices && (
              <div className={STYLES.CardRowLayout__otherPrices}>
                {otherPrices}
              </div>
            )}
          </BpkCard>
        </BpkTicket>
      </div>
    );
  }
}

export default CardRowLayout;
